import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, RefreshCw } from 'lucide-react';
import type { Team, Deal, PipelineStage, TeamMember } from '../../types';
import { getDeals, updateDealStage } from '../../services/deals';
import { getPipelineStages } from '../../services/pipeline';
import { getTeamMembers } from '../../services/teams';
import PipelineKanban from './PipelineKanban';
import DealDrawer from './DealDrawer';

interface DealsPageProps {
  team: Team;
  userId: string;
  isAdmin?: boolean;
}

const DealsPage: React.FC<DealsPageProps> = ({ team, userId, isAdmin }) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [initialStage, setInitialStage] = useState<string>('new');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [dealsData, stagesData, membersData] = await Promise.all([
      getDeals(team.id, { allTeams: isAdmin }),
      getPipelineStages(team.id),
      getTeamMembers(team.id),
    ]);
    setDeals(dealsData);
    setStages(stagesData);
    setMembers(membersData);
    setLoading(false);
  }, [team.id, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDealClick = (deal: Deal) => {
    setEditingDeal(deal);
    setInitialStage(deal.stage);
    setDrawerOpen(true);
  };

  const handleAddDeal = (stage: string) => {
    setEditingDeal(null);
    setInitialStage(stage);
    setDrawerOpen(true);
  };

  const handleDealDrop = async (dealId: string, newStage: string) => {
    const deal = deals.find((d) => d.id === dealId);
    if (!deal || deal.stage === newStage) return;

    setDeals(deals.map((d) => (d.id === dealId ? { ...d, stage: newStage } : d)));

    const success = await updateDealStage(dealId, newStage);
    if (!success) {
      setDeals(deals.map((d) => (d.id === dealId ? { ...d, stage: deal.stage } : d)));
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingDeal(null);
  };

  const totalValue = deals.reduce((sum, d) => sum + (d.value_monthly || 0), 0);

  return (
    <div className="animate-fadeIn h-full flex flex-col">
      <div className="flex items-center justify-between mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-white">Deals</h1>
          <p className="text-slate-400 text-sm mt-1">
            {deals.length} deals · £{totalValue.toLocaleString()}/mo pipeline value
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => handleAddDeal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Add Deal
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <Loader2 size={24} className="animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <PipelineKanban
            stages={stages}
            deals={deals}
            members={members}
            onDealClick={handleDealClick}
            onDealDrop={handleDealDrop}
            onAddDeal={handleAddDeal}
          />
        </div>
      )}

      {drawerOpen && (
        <DealDrawer
          deal={editingDeal}
          initialStage={initialStage}
          team={team}
          stages={stages}
          members={members}
          userId={userId}
          onClose={handleDrawerClose}
          onSaved={fetchData}
        />
      )}
    </div>
  );
};

export default DealsPage;
